"use client";

import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ALL_CHAINS, getChainName, getErrorDisplay, getExplorerTxUrl, getPaymentUrl, getStatusDisplay, isNativeToken, RELAY_SOLANA_MAINNET_CHAIN_ID, ZERO_ADDRESS } from "../../../../anyspend/index.js";
import { Badge, Button, CopyToClipboard, ShinyButton, Skeleton, TextLoop, TextShimmer, useAccountWallet, useB3Config, useModalStore, useProfile, useToastContext, useUnifiedChainSwitchAndExecute } from "../../../../global-account/react/index.js";
import { useRouter, useSearchParams } from "../../../../shared/react/hooks/index.js";
import { cn } from "../../../../shared/utils/index.js";
import centerTruncate from "../../../../shared/utils/centerTruncate.js";
import { formatTokenAmount } from "../../../../shared/utils/number.js";
import { toast } from "../../../../global-account/react/index.js";
import { WalletCoinbase, WalletMetamask, WalletPhantom, WalletTrust, WalletWalletConnect } from "@web3icons/react";
import { CheckIcon, ChevronRight, Copy, ExternalLink, Home, Loader2, RefreshCcw } from "lucide-react";
import { motion } from "motion/react";
import { QRCodeSVG } from "qrcode.react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TimeAgo from "react-timeago";
import { encodeFunctionData, erc20Abi } from "viem";
import { b3Viem } from "../../../../shared/constants/chains/b3Viem.js";
import { useWaitForTransactionReceipt, useWalletClient } from "wagmi";
import { usePhantomTransfer } from "../../hooks/usePhantomTransfer.js";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./Accordion.js";
import ConnectWalletPayment from "./ConnectWalletPayment.js";
import { CryptoPaymentMethodType } from "./CryptoPaymentMethod.js";
import { InsufficientDepositPayment } from "./InsufficientDepositPayment.js";
import { OrderDetailsCollapsible } from "./OrderDetailsCollapsible.js";
import { OrderStatus } from "./OrderStatus.js";
import PaymentVendorUI from "./PaymentVendorUI.js";
import { TransferCryptoDetails } from "./TransferCryptoDetails.js";
// Add this helper function near the top or just above the component
function getOrderSuccessText({
  order,
  tournament,
  formattedActualDstAmount,
  formattedExpectedDstAmount,
  dstToken,
  recipientName,
  centerTruncate
}) {
  const recipient = recipientName || centerTruncate(order.recipientAddress, 8);
  let actionText = "";
  switch (order.type) {
    case "swap":
    case "deposit_first":
      actionText = `sent ${formattedActualDstAmount || "--"} ${dstToken.symbol}`;
      return `Successfully ${actionText} to ${recipient}`;
    case "mint_nft":
      actionText = `minted ${order.metadata.nft.name}`;
      return `Successfully ${actionText} to ${recipient}`;
    case "join_tournament":
      actionText = `joined ${tournament?.name}`;
      return `Successfully ${actionText} for ${recipient}`;
    case "fund_tournament":
      actionText = `funded ${tournament?.name}`;
      return `Successfully ${actionText}`;
    case "hype_duel":
      actionText = `deposited ${formattedExpectedDstAmount || "--"} HYPE`;
      return `Successfully ${actionText} to ${recipient}`;
    case "custom":
      actionText = order.metadata.action || `executed contract`;
      return `Successfully ${actionText}`;
    case "x402_swap":
      actionText = `sent ${formattedActualDstAmount || "--"} ${dstToken.symbol}`;
      return `Successfully ${actionText} to ${recipient}`;
    case "custom_exact_in":
      actionText = `executed contract`;
      return `Successfully ${actionText}`;
    default:
      throw new Error("Invalid order type");
  }
}
/**
 * Rounds a token amount to 6 significant digits after the first non-zero in the decimal part.
 * Always rounds up (not standard rounding) if there are more than 6 significant digits.
 * For example:
 *    0.000123456 -> 0.000123456 (unchanged, as it has 6 significant digits)
 *    0.0001234561 -> 0.000123457 (rounded up to 6 significant digits)
 *    0.0001234564 -> 0.000123457 (always rounded up, even if digit < 5)
 *    1,234,567.000123456789 -> 1,234,567.000123457 (decimal part rounded up to 6 sig digits)
 *    1,234,567.000123123123 -> 1,234,567.000123124 (decimal part rounded up to 6 sig digits)
 * @param amount The amount value as a string
 * @returns The rounded amount string
 */
function roundTokenAmount(amount) {
  if (!amount) {
    return undefined;
  }
  // Split the number into whole and decimal parts
  const parts = amount.split(".");
  if (parts.length === 1) {
    // No decimal part
    return amount;
  }
  const wholePart = parts[0];
  const decimalPart = parts[1];
  // Check if decimal has 6 or fewer significant digits
  if (decimalPart.length <= 6) {
    return amount;
  }
  // Find the position of the first non-zero digit in the decimal part
  let firstNonZeroPos = 0;
  while (firstNonZeroPos < decimalPart.length && decimalPart[firstNonZeroPos] === "0") {
    firstNonZeroPos++;
  }
  // Calculate how many significant digits we have after the first non-zero
  const significantDigitsAfterFirstNonZero = decimalPart.length - firstNonZeroPos;
  // If we have 6 or fewer significant digits, return as is
  if (significantDigitsAfterFirstNonZero <= 6) {
    return amount;
  }
  // We need to round to 6 significant digits after the first non-zero
  // Keep all leading zeros plus 6 significant digits
  const keepLength = firstNonZeroPos + 6;
  // Always round up if there are more digits
  const shouldRoundUp = decimalPart.length > keepLength;
  // Create array of digits we're keeping (to handle carry)
  const digits = decimalPart.substring(0, keepLength).split("");
  // Apply rounding (always round up)
  if (shouldRoundUp) {
    // Start from the last position and carry as needed
    let i = digits.length - 1;
    let carry = 1;
    while (i >= 0 && carry > 0) {
      const digit = parseInt(digits[i], 10) + carry;
      if (digit === 10) {
        digits[i] = "0";
        carry = 1;
      } else {
        digits[i] = digit.toString();
        carry = 0;
      }
      i--;
    }
    // Handle carry into the whole part if needed
    if (carry > 0) {
      return `${(parseInt(wholePart, 10) + 1).toString()}.${digits.join("")}`;
    }
  }
  // Join the parts back together
  const roundedDecimalPart = digits.join("");
  return `${wholePart}.${roundedDecimalPart}`;
}
export const OrderDetails = memo(function OrderDetails({
  mode = "modal",
  order,
  depositTxs,
  relayTxs,
  executeTx,
  refundTxs,
  cryptoPaymentMethod,
  selectedCryptoPaymentMethod,
  onPaymentMethodChange,
  onBack,
  disableUrlParamManagement = false,
  points,
  returnToHomeUrl,
  returnHomeLabel,
  classes
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    setHeaderMode
  } = useToastContext();
  useEffect(() => {
    setHeaderMode(true);
    return () => setHeaderMode(false);
  }, [setHeaderMode]);
  // Get theme from B3Provider context
  const {
    theme
  } = useB3Config();
  const colorMode = theme || "light";
  // Read crypto payment method from URL parameters
  const cryptoPaymentMethodFromUrl = searchParams.get("cryptoPaymentMethod");
  const effectiveCryptoPaymentMethod = selectedCryptoPaymentMethod !== CryptoPaymentMethodType.NONE ? selectedCryptoPaymentMethod : cryptoPaymentMethod || cryptoPaymentMethodFromUrl || CryptoPaymentMethodType.NONE;
  const setB3ModalOpen = useModalStore(state => state.setB3ModalOpen);
  const srcToken = order.metadata.srcToken;
  const dstToken = order.metadata.dstToken;
  const nft = order.type === "mint_nft" ? order.metadata.nft : undefined;
  const tournament = order.type === "join_tournament" || order.type === "fund_tournament" ? order.metadata.tournament : undefined;
  const profile = useProfile({
    address: order.recipientAddress
  });
  const recipientName = profile.data?.name?.replace(/\.b3\.fun/g, "");
  const account = useAccountWallet();
  const {
    data: walletClient
  } = useWalletClient();
  const [txHash, setTxHash] = useState();
  const [showQRCode, setShowQRCode] = useState(false);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(true);
  const {
    isLoading: txLoading,
    isSuccess: txSuccess
  } = useWaitForTransactionReceipt({
    hash: txHash
  });
  const {
    switchChainAndExecuteWithEOA,
    switchChainAndExecute,
    isSwitchingOrExecuting
  } = useUnifiedChainSwitchAndExecute();
  // Track if auto-payment was attempted to avoid re-triggering
  const autoPaymentAttempted = useRef(false);
  // Track if component is ready for auto-payment (all data loaded)
  const [isComponentReady, setIsComponentReady] = useState(false);
  const roundedUpSrcAmount = useMemo(() => {
    // Display the full transfer amount without rounding since users need to see the exact value they're transferring.
    // Use 21 significant digits (max allowed by Intl.NumberFormat)
    const formattedSrcAmount = srcToken ? formatTokenAmount(BigInt(order.srcAmount), srcToken.decimals, 21, false) : undefined;
    return roundTokenAmount(formattedSrcAmount);
  }, [order.srcAmount, srcToken]);
  // Calculate deposit amounts - moved here to be used in useCallback hooks
  const depositedAmount = useMemo(() => {
    return depositTxs ? depositTxs.reduce((acc, curr) => acc + BigInt(curr.amount), BigInt(0)) : BigInt(0);
  }, [depositTxs]);
  const depositDeficit = useMemo(() => {
    return BigInt(order.srcAmount) - depositedAmount;
  }, [order.srcAmount, depositedAmount]);
  const depositEnoughAmount = useMemo(() => {
    return depositDeficit <= BigInt(0);
  }, [depositDeficit]);
  const formattedDepositDeficit = useMemo(() => {
    return formatTokenAmount(BigInt(depositDeficit), srcToken.decimals);
  }, [depositDeficit, srcToken.decimals]);
  // Unified payment handler for both EOA and AA wallets
  const handleUnifiedPaymentProcess = useCallback(async () => {
    let txData;
    let value;
    let to;
    // Use the existing depositDeficit calculation to determine amount to send
    const amountToSend = depositDeficit > BigInt(0) ? depositDeficit : BigInt(order.srcAmount);
    if (isNativeToken(order.srcTokenAddress)) {
      // Native token transfer
      to = order.globalAddress;
      value = amountToSend;
    } else {
      // ERC20 token transfer - encode the transfer function call using proper ABI
      txData = encodeFunctionData({
        abi: erc20Abi,
        functionName: "transfer",
        args: [order.globalAddress, amountToSend]
      });
      to = order.srcTokenAddress;
      value = BigInt(0);
    }
    // Use appropriate execution method based on payment method
    let txHash_0;
    if (effectiveCryptoPaymentMethod === CryptoPaymentMethodType.GLOBAL_WALLET) {
      txHash_0 = await switchChainAndExecute(order.srcChain, {
        to,
        data: txData,
        value
      });
    } else {
      txHash_0 = await switchChainAndExecuteWithEOA(order.srcChain, {
        to,
        data: txData,
        value
      });
    }
    if (txHash_0) {
      setTxHash(txHash_0);
    }
  }, [order, switchChainAndExecuteWithEOA, switchChainAndExecute, depositDeficit, effectiveCryptoPaymentMethod]);
  // Use Phantom transfer hook for Solana payments
  const {
    initiateTransfer: initiatePhantomTransfer,
    getConnectedAddress: getPhantomAddress
  } = usePhantomTransfer();
  // Main payment handler that triggers chain switch and payment
  const handlePayment = useCallback(async () => {
    console.log("Initiating payment process. Target chain:", order.srcChain, "Current chain:", walletClient?.chain?.id);
    const amountToSend_0 = depositDeficit > BigInt(0) ? depositDeficit.toString() : order.srcAmount;
    if (order.srcChain === RELAY_SOLANA_MAINNET_CHAIN_ID) {
      // Solana payment flow
      await initiatePhantomTransfer({
        amountLamports: amountToSend_0,
        tokenAddress: order.srcTokenAddress,
        recipientAddress: order.globalAddress
      });
    } else {
      // EVM payment flow (EOA and AA wallets)
      // Note: Hyperliquid is NOT supported as source chain, only as destination chain
      await handleUnifiedPaymentProcess();
    }
  }, [order, walletClient?.chain?.id, depositDeficit, handleUnifiedPaymentProcess, initiatePhantomTransfer]);
  // When waitingForDeposit is true, we show a message to the user to wait for the deposit to be processed.
  const setWaitingForDeposit = useCallback(() => {
    if (disableUrlParamManagement) return;
    const params = new URLSearchParams(searchParams.toString());
    params.set("waitingForDeposit", "true");
    router.push(`?${params}`);
  }, [router, searchParams, disableUrlParamManagement]);
  // Clean up URL parameters before closing modal or navigating back
  const cleanupUrlParams = useCallback(() => {
    if (disableUrlParamManagement) return;
    const params_0 = new URLSearchParams(searchParams.toString());
    params_0.delete("waitingForDeposit");
    params_0.delete("orderId");
    params_0.delete("paymentMethod");
    // Only update URL if params were actually removed
    if (params_0.toString() !== searchParams.toString()) {
      router.push(`?${params_0}`);
    }
  }, [router, searchParams, disableUrlParamManagement]);
  // Helper functions that clean up URL params before executing actions
  const handleCloseModal = useCallback(() => {
    cleanupUrlParams();
    setB3ModalOpen(false);
  }, [cleanupUrlParams, setB3ModalOpen]);
  const handleBack = useCallback(() => {
    cleanupUrlParams();
    onBack?.();
  }, [cleanupUrlParams, onBack]);
  // Handle "Return to Home" click - redirects to custom URL if provided
  const handleReturnToHome = useCallback(() => {
    if (returnToHomeUrl) {
      // Validate URL to prevent Open Redirect / XSS attacks
      try {
        const url = new URL(returnToHomeUrl, window.location.origin);
        // Only allow http/https protocols
        if (url.protocol === "http:" || url.protocol === "https:") {
          window.location.href = url.href;
          return;
        }
      } catch {
        // If URL parsing fails, check if it's a safe relative URL
        if (returnToHomeUrl.startsWith("/") && !returnToHomeUrl.startsWith("//")) {
          window.location.href = returnToHomeUrl;
          return;
        }
      }
      // Fallback to handleBack if URL is not safe
      handleBack();
    } else {
      handleBack();
    }
  }, [returnToHomeUrl, handleBack]);
  // Reusable "Return to Home" / "Close" button
  const returnHomeOrCloseButton = _jsx("button", {
    className: classes?.returnButton || "order-close-button order-details-close-btn bg-as-brand flex w-full items-center justify-center gap-2 rounded-lg p-2 font-semibold text-white",
    onClick: returnToHomeUrl ? handleReturnToHome : mode === "page" ? handleBack : handleCloseModal,
    children: returnHomeLabel ? returnHomeLabel : mode === "page" ? _jsxs(_Fragment, {
      children: ["Return to Home ", _jsx(Home, {
        className: "ml-2 h-4 w-4"
      })]
    }) : "Close"
  });
  useEffect(() => {
    if (txSuccess) {
      toast.success("Transaction successful! We are processing your order.", {
        duration: 10000
      });
      setWaitingForDeposit();
      setTxHash(undefined);
    }
  }, [setWaitingForDeposit, txSuccess]);
  // Get connected Phantom wallet address if available
  const phantomWalletAddress = useMemo(() => getPhantomAddress(), [getPhantomAddress]);
  // Calculate status display before using it
  const {
    text: statusText,
    status: statusDisplay
  } = getStatusDisplay(order);
  // Memoize the payable state calculation to avoid recalculating on every render
  const isPayableState = useMemo(() => {
    const waitingForDeposit = new URLSearchParams(window.location.search).get("waitingForDeposit") === "true";
    return refundTxs.length === 0 && !executeTx && !(relayTxs.length > 0 && relayTxs.every(tx => tx.status === "success")) && !depositTxs?.length && !waitingForDeposit && statusDisplay === "processing" && !order.onrampMetadata && (effectiveCryptoPaymentMethod === CryptoPaymentMethodType.CONNECT_WALLET || effectiveCryptoPaymentMethod === CryptoPaymentMethodType.GLOBAL_WALLET);
  }, [refundTxs.length, executeTx, relayTxs, depositTxs?.length, statusDisplay, order.onrampMetadata, effectiveCryptoPaymentMethod]);
  // Mark component as ready once all critical data is available
  // This ensures we don't trigger payment before the component has fully initialized
  useEffect(() => {
    if (!isComponentReady && srcToken && dstToken && statusDisplay) {
      setIsComponentReady(true);
    }
  }, [isComponentReady, srcToken, dstToken, statusDisplay]);
  // Auto-trigger payment when component is ready and order is in payable state
  // This effect only runs when isPayableState or isComponentReady changes
  useEffect(() => {
    // Only trigger payment if:
    // 1. We haven't attempted payment yet
    // 2. Component is fully ready (all data loaded)
    // 3. Order is in a payable state
    if (!autoPaymentAttempted.current && isComponentReady && isPayableState) {
      autoPaymentAttempted.current = true;
      handlePayment();
    }
  }, [isPayableState, isComponentReady, handlePayment]);
  // Auto-redirect to redirectUrl when order is executed (for onramp orders)
  useEffect(() => {
    if (order.status === "executed" && order.onrampMetadata?.redirectUrl) {
      const baseUrl = order.onrampMetadata.redirectUrl;
      try {
        const url_0 = new URL(baseUrl);
        // Prevent Open Redirect vulnerabilities by ensuring the protocol is http or https
        if (url_0.protocol !== "http:" && url_0.protocol !== "https:") {
          console.error(`Attempted redirect to a URL with an invalid protocol: ${url_0.protocol}`);
          return;
        }
        const redirectUrl = `${baseUrl.replace(/\/$/, "")}/${order.id}`;
        window.location.href = redirectUrl;
      } catch (error) {
        console.error("Invalid redirect URL provided:", baseUrl, error);
      }
    }
  }, [order.status, order.onrampMetadata?.redirectUrl, order.id]);
  if (!srcToken || !dstToken) {
    return _jsx("div", {
      children: "Loading..."
    });
  }
  const expectedDstAmount = order.type === "mint_nft" || order.type === "join_tournament" || order.type === "fund_tournament" ? "0" : order.type === "custom" || order.type === "deposit_first" ? order.payload.amount?.toString() || "0" : order.payload.expectedDstAmount.toString();
  const formattedExpectedDstAmount = formatTokenAmount(BigInt(expectedDstAmount), dstToken.decimals);
  const actualDstAmount = order.settlement?.actualDstAmount;
  const formattedActualDstAmount = actualDstAmount ? formatTokenAmount(BigInt(actualDstAmount), dstToken.decimals) : undefined;
  if (refundTxs.length > 0) {
    return _jsxs(_Fragment, {
      children: [_jsx(OrderStatus, {
        order: order,
        selectedCryptoPaymentMethod: effectiveCryptoPaymentMethod
      }), _jsx(OrderDetailsCollapsible, {
        order: order,
        dstToken: dstToken,
        tournament: tournament,
        nft: nft,
        recipientName: recipientName,
        formattedExpectedDstAmount: formattedExpectedDstAmount,
        points: points,
        isOpen: orderDetailsOpen,
        onOpenChange: setOrderDetailsOpen
      }), _jsx(Accordion, {
        type: "single",
        collapsible: true,
        className: "order-details-accordion w-full",
        children: _jsxs(AccordionItem, {
          value: "refund-details",
          className: "order-details-refund-item",
          children: [_jsx(AccordionTrigger, {
            className: "accordion-trigger",
            children: "Transaction Details"
          }), _jsx(AccordionContent, {
            className: "accordion-content pl-2",
            children: _jsxs("div", {
              className: "relative flex w-full flex-col gap-4",
              children: [_jsx("div", {
                className: "bg-as-surface-secondary absolute bottom-2 left-4 top-2 z-[5] w-2",
                children: _jsx(motion.div, {
                  className: "bg-as-border-primary absolute left-[2px] top-0 z-10 w-[3px]",
                  initial: {
                    height: "0%"
                  },
                  animate: {
                    height: "100%"
                  },
                  transition: {
                    duration: 1.5,
                    ease: "easeInOut"
                  }
                })
              }), depositTxs ? depositTxs.map(dTx => _jsx(TransactionDetails, {
                title: order.onrampMetadata?.vendor === "stripe-web2" ? `Received payment` : `Received ${formatTokenAmount(BigInt(dTx.amount), srcToken.decimals)} ${srcToken.symbol}`,
                chainId: order.srcChain,
                tx: dTx,
                isProcessing: false
              }, dTx.txHash)) : null, refundTxs ? refundTxs.map(rTx => _jsx(TransactionDetails, {
                title: `Refunded ${formatTokenAmount(BigInt(rTx.amount), srcToken.decimals)} ${srcToken.symbol}`,
                chainId: order.srcChain,
                tx: rTx,
                isProcessing: false
              }, rTx.txHash)) : null]
            })
          })]
        })
      }), order.errorDetails && _jsx("div", {
        className: "flex justify-center",
        children: _jsx("span", {
          className: "text-as-primary/50 text-center text-sm",
          style: {
            maxWidth: "40ch"
          },
          children: getErrorDisplay(order.errorDetails)
        })
      }), returnHomeOrCloseButton]
    });
  }
  if (executeTx) {
    return _jsxs(_Fragment, {
      children: [_jsx(OrderStatus, {
        order: order,
        selectedCryptoPaymentMethod: effectiveCryptoPaymentMethod
      }), _jsx(OrderDetailsCollapsible, {
        order: order,
        dstToken: dstToken,
        tournament: tournament,
        nft: nft,
        recipientName: recipientName,
        formattedExpectedDstAmount: formattedExpectedDstAmount,
        points: points,
        isOpen: orderDetailsOpen,
        onOpenChange: setOrderDetailsOpen
      }), _jsx(Accordion, {
        type: "single",
        collapsible: true,
        className: "order-details-accordion w-full",
        children: _jsxs(AccordionItem, {
          value: "execute-details",
          className: "order-details-execute-item",
          children: [_jsx(AccordionTrigger, {
            className: "accordion-trigger",
            children: "Transaction Details"
          }), _jsx(AccordionContent, {
            className: "accordion-content pl-2",
            children: _jsxs("div", {
              className: "relative flex w-full flex-col gap-4",
              children: [_jsx("div", {
                className: "bg-as-surface-secondary absolute bottom-2 left-4 top-2 z-[5] w-2",
                children: _jsx(motion.div, {
                  className: "bg-as-border-primary absolute left-[2px] top-0 z-10 w-[3px]",
                  initial: {
                    height: "0%"
                  },
                  animate: {
                    height: "100%"
                  },
                  transition: {
                    duration: 1.5,
                    ease: "easeInOut"
                  }
                })
              }), depositTxs ? depositTxs.map(dTxs => _jsx(TransactionDetails, {
                title: order.onrampMetadata?.vendor === "stripe-web2" ? `Received payment` : `Received ${formatTokenAmount(BigInt(dTxs.amount), srcToken.decimals)} ${srcToken.symbol}`,
                chainId: order.srcChain,
                tx: dTxs,
                isProcessing: false
              }, dTxs.txHash)) : null, relayTxs ? relayTxs.map(relayTx => _jsx(TransactionDetails, {
                title: "Processed Swap",
                chainId: relayTx.chain,
                tx: relayTx,
                delay: 0.5,
                isProcessing: false
              }, relayTx.txHash)) : null, _jsx(TransactionDetails, {
                title: order.type === "swap" || order.type === "deposit_first" ? "Processed Swap" : order.type === "mint_nft" ? "Minted NFT" : order.type === "join_tournament" ? "Joined Tournament" : order.type === "fund_tournament" ? "Funded Tournament" : "Processed Order",
                chainId: order.dstChain,
                tx: executeTx,
                isProcessing: false,
                delay: 1
              })]
            })
          })]
        })
      }), _jsx("div", {
        className: "flex w-full flex-col gap-8",
        children: _jsx(Button, {
          variant: "link",
          asChild: true,
          children: _jsxs("a", {
            href: getExplorerTxUrl(order.dstChain, executeTx.txHash),
            target: "_blank",
            className: "order-success-text text-as-primary/70 hover:text-as-primary",
            style: {
              whiteSpace: "normal"
            },
            children: [getOrderSuccessText({
              order,
              tournament,
              formattedActualDstAmount: formattedActualDstAmount,
              formattedExpectedDstAmount: formattedExpectedDstAmount,
              dstToken,
              recipientName,
              centerTruncate
            }), _jsx(ExternalLink, {
              className: "ml-2 h-4 w-4"
            })]
          })
        })
      }), order.type === "join_tournament" && order.status === "executed" && _jsxs(ShinyButton, {
        accentColor: "hsl(var(--as-brand))",
        textColor: "text-white",
        className: "flex w-full items-center gap-2",
        disabled: txLoading || isSwitchingOrExecuting,
        onClick: handleCloseModal,
        children: [_jsx("span", {
          className: "pl-4",
          children: "Continue to Tournament"
        }), _jsx(ChevronRight, {
          className: "h-4 w-4"
        })]
      }), order.status === "executed" && returnHomeOrCloseButton]
    });
  }
  if (relayTxs.length > 0 && relayTxs.every(tx_0 => tx_0.status === "success")) {
    return _jsxs(_Fragment, {
      children: [_jsx(OrderStatus, {
        order: order,
        selectedCryptoPaymentMethod: effectiveCryptoPaymentMethod
      }), _jsx(OrderDetailsCollapsible, {
        order: order,
        dstToken: dstToken,
        tournament: tournament,
        nft: nft,
        recipientName: recipientName,
        formattedExpectedDstAmount: formattedExpectedDstAmount,
        points: points,
        isOpen: orderDetailsOpen,
        onOpenChange: setOrderDetailsOpen
      }), _jsx(Accordion, {
        type: "single",
        collapsible: true,
        className: "order-details-accordion w-full",
        children: _jsxs(AccordionItem, {
          value: "more-details",
          className: "order-details-more-item",
          children: [_jsx(AccordionTrigger, {
            className: "accordion-trigger",
            children: "More Details"
          }), _jsx(AccordionContent, {
            className: "accordion-content pl-2",
            children: _jsxs("div", {
              className: "relative flex w-full flex-col gap-4",
              children: [_jsx("div", {
                className: "bg-as-surface-secondary absolute bottom-2 left-4 top-2 z-[5] w-2",
                children: _jsx(motion.div, {
                  className: "bg-as-border-primary absolute left-[2px] top-0 z-10 w-[3px]",
                  initial: {
                    height: "0%"
                  },
                  animate: {
                    height: "100%"
                  },
                  transition: {
                    duration: 1.5,
                    ease: "easeInOut"
                  }
                })
              }), depositTxs ? depositTxs.map(dTxs_0 => _jsx(TransactionDetails, {
                title: order.onrampMetadata?.vendor === "stripe-web2" ? `Received payment` : `Received ${formatTokenAmount(BigInt(dTxs_0.amount), srcToken.decimals)} ${srcToken.symbol}`,
                chainId: order.srcChain,
                tx: dTxs_0,
                isProcessing: false
              }, dTxs_0.txHash)) : null, relayTxs.map(relayTx_0 => _jsx(TransactionDetails, {
                title: relayTx_0.chain === order.srcChain ? `Process swap on ${getChainName(relayTx_0.chain)}` : `Received on ${getChainName(relayTx_0.chain)}`,
                chainId: relayTx_0.chain,
                isProcessing: false,
                tx: relayTx_0,
                delay: 0.5
              }, relayTx_0.txHash)), order.status === "executing" && _jsx(TransactionDetails, {
                title: order.type === "swap" || order.type === "x402_swap" || order.type === "deposit_first" ? "Processing swap" : order.type === "mint_nft" ? "Minting NFT" : order.type === "join_tournament" ? "Joining Tournament" : order.type === "fund_tournament" ? "Funding Tournament" : order.type === "hype_duel" ? "Depositing Hype Duel" : order.type === "custom" || order.type === "custom_exact_in" ? "Executing Contract" : "Processing Bridge",
                chainId: order.dstChain,
                isProcessing: true,
                tx: null,
                delay: 1
              })]
            })
          })]
        })
      }), order.type === "join_tournament" && order.status === "executed" && _jsxs(ShinyButton, {
        accentColor: "hsl(var(--as-brand))",
        textColor: "text-white",
        className: "flex w-full items-center gap-2",
        disabled: txLoading || isSwitchingOrExecuting,
        onClick: handleCloseModal,
        children: [_jsx("span", {
          className: "pl-4",
          children: "Continue to Tournament"
        }), _jsx(ChevronRight, {
          className: "h-4 w-4"
        })]
      }), order.status === "executed" && returnHomeOrCloseButton]
    });
  }
  // This boolean indicates that user finish payment, and waiting for the deposit to be confirmed. We get this from query params (waitingForDeposit=true)
  const waitingForDeposit_0 = new URLSearchParams(window.location.search).get("waitingForDeposit") === "true";
  if (depositTxs?.length || waitingForDeposit_0) {
    return _jsxs(_Fragment, {
      children: [_jsx(OrderStatus, {
        order: order,
        selectedCryptoPaymentMethod: effectiveCryptoPaymentMethod
      }), _jsx(OrderDetailsCollapsible, {
        order: order,
        dstToken: dstToken,
        tournament: tournament,
        nft: nft,
        recipientName: recipientName,
        formattedExpectedDstAmount: formattedExpectedDstAmount,
        points: points,
        isOpen: orderDetailsOpen,
        onOpenChange: setOrderDetailsOpen
      }), _jsx(Accordion, {
        type: "single",
        collapsible: true,
        className: "order-details-accordion w-full",
        children: _jsxs(AccordionItem, {
          value: "deposit-details",
          className: "order-details-deposit-item",
          children: [_jsx(AccordionTrigger, {
            className: "accordion-trigger",
            children: "Transaction Details"
          }), _jsx(AccordionContent, {
            className: "accordion-content pl-2",
            children: _jsxs("div", {
              className: "relative flex w-full flex-col gap-6",
              children: [_jsx("div", {
                className: "bg-as-surface-secondary absolute bottom-2 left-4 top-2 z-[5] w-2",
                children: _jsx(motion.div, {
                  className: "from-as-brand/50 absolute left-[2px] top-0 z-10 w-[3px] bg-gradient-to-b from-20% via-purple-500/50 via-80% to-transparent",
                  initial: {
                    height: "0%"
                  },
                  animate: {
                    height: "100%"
                  },
                  transition: {
                    duration: 1,
                    ease: "easeInOut"
                  }
                })
              }), (depositTxs || []).map((dTxs_1, index) => _jsx(TransactionDetails, {
                title: order.onrampMetadata?.vendor === "stripe-web2" ? `Received payment` : `Received ${formatTokenAmount(BigInt(dTxs_1.amount), srcToken.decimals)} ${srcToken.symbol}`,
                chainId: order.srcChain,
                tx: dTxs_1,
                isProcessing: index < (depositTxs || []).length - 1 ? false : !depositEnoughAmount
              }, dTxs_1.txHash)), statusDisplay === "failure" ? _jsx(TransactionDetails, {
                title: statusText,
                chainId: order.srcChain,
                tx: null,
                isProcessing: false,
                delay: 0.5
              }) : depositEnoughAmount ? _jsx(TransactionDetails, {
                title: order.type === "swap" || order.type === "deposit_first" ? "Processing swap" : order.type === "mint_nft" ? "Minting NFT" : order.type === "join_tournament" ? "Joining Tournament" : order.type === "fund_tournament" ? "Funding Tournament" : "Processing Transaction",
                chainId: order.srcChain,
                tx: null,
                isProcessing: true,
                delay: 0.5
              }) : _jsx(TransactionDetails, {
                title: order.onrampMetadata?.vendor === "stripe-web2" ? `Waiting for payment` : `Waiting for deposit ${formattedDepositDeficit} ${srcToken.symbol}`,
                chainId: order.srcChain,
                tx: null,
                isProcessing: true,
                delay: 0.5
              })]
            })
          })]
        })
      }), depositTxs?.length > 0 && !depositEnoughAmount && order.status === "scanning_deposit_transaction" && _jsx(InsufficientDepositPayment, {
        order: order,
        srcToken: srcToken,
        depositDeficit: depositDeficit,
        phantomWalletAddress: phantomWalletAddress,
        txLoading: txLoading,
        isSwitchingOrExecuting: isSwitchingOrExecuting,
        onPayment: handlePayment
      }), _jsxs("button", {
        className: classes?.backButton || "text-b3-primary-blue hover:text-b3-primary-blue/50 order-details-cancel-btn flex w-full items-center justify-center gap-2 underline",
        onClick: handleBack,
        children: [_jsx(RefreshCcw, {
          className: "ml-2 h-4 w-4"
        }), " Cancel and start over"]
      })]
    });
  }
  return _jsxs(_Fragment, {
    children: [_jsx(OrderStatus, {
      order: order,
      selectedCryptoPaymentMethod: effectiveCryptoPaymentMethod
    }), statusDisplay === "processing" && _jsx(_Fragment, {
      children: order.onrampMetadata && order.onrampMetadata.vendor !== "none" ? _jsx(PaymentVendorUI, {
        order: order,
        dstTokenSymbol: dstToken.symbol
      }) : effectiveCryptoPaymentMethod === CryptoPaymentMethodType.CONNECT_WALLET || effectiveCryptoPaymentMethod === CryptoPaymentMethodType.GLOBAL_WALLET ? _jsx(ConnectWalletPayment, {
        order: order,
        onPayment: handlePayment,
        onCancel: handleBack,
        txLoading: txLoading,
        isSwitchingOrExecuting: isSwitchingOrExecuting,
        phantomWalletAddress: phantomWalletAddress,
        tournament: tournament,
        nft: nft,
        cryptoPaymentMethod: effectiveCryptoPaymentMethod,
        onPaymentMethodChange: onPaymentMethodChange
      }) : effectiveCryptoPaymentMethod === CryptoPaymentMethodType.TRANSFER_CRYPTO ?
      // Transfer Crypto Payment Method - Show new card-based UI
      _jsx(TransferCryptoDetails, {
        order: order,
        recipientName: recipientName,
        srcToken: srcToken,
        dstToken: dstToken,
        tournament: tournament,
        nft: nft,
        onBack: handleBack,
        onPaymentMethodChange: onPaymentMethodChange
      }) : _jsxs("div", {
        className: "order-details-payment-section relative flex w-full flex-1 flex-col",
        children: [_jsxs("div", {
          className: "order-details-amount-section flex flex-col gap-1",
          children: [_jsx("span", {
            className: "text-as-primary/50 order-details-amount-label",
            children: "Please send"
          }), _jsxs("div", {
            className: "order-details-amount-container flex w-full flex-wrap items-center gap-6 sm:justify-between sm:gap-0",
            children: [_jsx(CopyToClipboard, {
              text: roundedUpSrcAmount,
              onCopy: () => {
                toast.success("Copied to clipboard");
              },
              children: _jsxs("div", {
                className: "order-details-amount-display flex items-center gap-2",
                children: [_jsxs("strong", {
                  className: "border-as-brand text-as-primary order-details-amount-text border-b-2 pb-1 text-2xl font-semibold sm:text-xl",
                  children: [roundedUpSrcAmount, " ", srcToken.symbol]
                }), _jsx(Copy, {
                  className: "text-as-primary/50 hover:text-as-primary order-details-copy-icon h-5 w-5 cursor-pointer transition-all duration-200"
                })]
              })
            }), _jsxs(Badge, {
              variant: "outline",
              className: "flex h-10 items-center gap-2 px-3 py-1 pr-2 text-sm",
              children: ["on ", getChainName(order.srcChain), _jsx("img", {
                src: ALL_CHAINS[order.srcChain].logoUrl,
                alt: getChainName(order.srcChain),
                className: cn("h-6 rounded-full", order.srcChain === b3Viem.id && "h-5 rounded-none")
              })]
            })]
          }), _jsx("span", {
            className: "text-as-primary/50 order-details-address-label mb-1 mt-2",
            children: " to the address:"
          })]
        }), _jsx(CopyToClipboard, {
          text: order.globalAddress,
          onCopy: () => {
            toast.success("Copied to clipboard");
          },
          children: _jsxs("div", {
            className: "bg-b3-react-background border-b3-react-border hover:border-as-brand order-details-address-container group flex cursor-pointer items-center justify-between gap-4 rounded-lg border p-3 px-4 shadow-md transition-all duration-200",
            children: [_jsx("div", {
              className: "text-as-primary order-details-address-text overflow-hidden text-ellipsis whitespace-nowrap text-sm",
              children: order.globalAddress
            }), _jsx(Copy, {
              className: "group-hover:text-as-brand text-as-primary/50 order-details-address-copy-icon h-5 w-5 cursor-pointer transition-all duration-200"
            })]
          })
        }), (account?.address || phantomWalletAddress) && !showQRCode ? _jsx("div", {
          className: "mb-4 mt-8 flex w-full flex-col items-center gap-4",
          children: _jsxs(_Fragment, {
            children: [_jsxs("div", {
              className: "relative flex w-full flex-col items-center gap-2",
              children: [_jsx(ShinyButton, {
                accentColor: "hsl(var(--as-brand))",
                textColor: "text-white",
                className: "flex w-5/6 items-center gap-2 sm:px-0",
                disabled: txLoading || isSwitchingOrExecuting,
                onClick: handlePayment,
                children: txLoading ? _jsxs(_Fragment, {
                  children: ["Transaction Pending", _jsx(Loader2, {
                    className: "ml-2 h-5 w-5 animate-spin"
                  })]
                }) : _jsxs(_Fragment, {
                  children: [_jsx("span", {
                    className: "pl-4 text-lg md:text-sm",
                    children: order.srcChain === RELAY_SOLANA_MAINNET_CHAIN_ID && phantomWalletAddress ? "Pay from Phantom Wallet" : "Pay from Connected Wallet"
                  }), _jsx(ChevronRight, {
                    className: "h-4 w-4"
                  })]
                })
              }), _jsxs("span", {
                className: "label-style text-as-primary/50 text-xs",
                children: ["Connected to:", " ", order.srcChain === RELAY_SOLANA_MAINNET_CHAIN_ID && phantomWalletAddress ? centerTruncate(phantomWalletAddress, 6) : centerTruncate(account?.address || "", 6)]
              })]
            }), _jsxs("div", {
              className: "flex w-full flex-col items-center gap-2",
              children: [_jsxs(ShinyButton, {
                accentColor: colorMode === "dark" ? "#ffffff" : "#000000",
                className: "flex w-5/6 items-center gap-2 sm:px-0",
                onClick: () => setShowQRCode(true),
                children: [_jsx("span", {
                  className: "pl-4 text-lg md:text-sm",
                  children: "Pay from a different wallet"
                }), _jsx(ChevronRight, {
                  className: "h-4 w-4"
                })]
              }), _jsxs("div", {
                className: "flex items-center gap-2",
                children: [_jsx(WalletMetamask, {
                  className: "h-5 w-5",
                  variant: "branded"
                }), _jsx(WalletCoinbase, {
                  className: "h-5 w-5",
                  variant: "branded"
                }), _jsx(WalletPhantom, {
                  className: "h-5 w-5",
                  variant: "branded"
                }), _jsx(WalletTrust, {
                  className: "h-5 w-5",
                  variant: "branded"
                }), _jsx(WalletWalletConnect, {
                  className: "h-5 w-5",
                  variant: "branded"
                }), _jsx("span", {
                  className: "label-style text-as-primary/30 text-xs",
                  children: "& more"
                })]
              })]
            })]
          })
        }) :
        // Default case - existing QR code flow
        _jsx(motion.div, {
          initial: {
            opacity: 0,
            filter: "blur(10px)"
          },
          animate: {
            opacity: 1,
            filter: "blur(0px)"
          },
          transition: {
            duration: 0.5,
            ease: "easeInOut"
          },
          className: "flex w-full items-center justify-evenly gap-4",
          children: _jsxs("div", {
            className: "order-details-qr-container mt-8 flex flex-col items-center rounded-lg bg-white p-6 pb-3",
            children: [_jsx(QRCodeSVG, {
              value: getPaymentUrl(order.globalAddress, BigInt(order.srcAmount), order.srcTokenAddress === ZERO_ADDRESS ? srcToken?.symbol || "ETH" : order.srcTokenAddress, order.srcChain, srcToken?.decimals),
              className: "order-details-qr-code max-w-[200px]"
            }), _jsxs("div", {
              className: "order-details-qr-wallets mt-3 flex items-center justify-center gap-2 text-sm",
              children: [_jsx("span", {
                className: "label-style text-as-brand/70 text-sm",
                children: "Scan with"
              }), _jsxs(TextLoop, {
                interval: 3,
                children: [_jsx(WalletMetamask, {
                  className: "h-5 w-5",
                  variant: "branded"
                }), _jsx(WalletCoinbase, {
                  className: "h-5 w-5",
                  variant: "branded"
                }), _jsx(WalletPhantom, {
                  className: "h-5 w-5",
                  variant: "branded"
                }), _jsx(WalletTrust, {
                  className: "h-5 w-5",
                  variant: "branded"
                })]
              })]
            })]
          })
        })]
      })
    }), _jsxs("div", {
      className: "order-details-time-remaining flex w-full items-center justify-center gap-1 text-sm",
      children: [_jsx("div", {
        className: "text-as-primary/30 order-details-time-label",
        children: "Time remaining:"
      }), _jsx("div", {
        className: "text-as-primary order-details-time-value",
        children: depositEnoughAmount ? "Received" : order.status === "expired" ? "Expired" : _jsx(TimeAgo, {
          date: new Date(order.expiredAt),
          live: true
        })
      })]
    }), statusDisplay !== "processing" && _jsx(OrderDetailsCollapsible, {
      order: order,
      dstToken: dstToken,
      tournament: tournament,
      nft: nft,
      recipientName: recipientName,
      formattedExpectedDstAmount: formattedExpectedDstAmount,
      points: points,
      isOpen: orderDetailsOpen,
      onOpenChange: setOrderDetailsOpen
    }), _jsxs("button", {
      className: classes?.backButton || "text-b3-primary-blue hover:text-b3-primary-blue/50 order-details-cancel-btn flex w-full items-center justify-center gap-2 underline",
      onClick: handleBack,
      children: [_jsx(RefreshCcw, {
        className: "ml-2 h-4 w-4"
      }), " Cancel and start over"]
    })]
  });
});
function TransactionDetails({
  title,
  chainId,
  tx,
  isProcessing,
  delay
}) {
  return _jsxs("div", {
    className: "order-details-transaction-item relative flex w-full flex-1 items-center justify-between gap-4",
    children: [_jsxs("div", {
      className: "order-details-transaction-content flex grow items-center gap-4",
      children: [_jsx(motion.div, {
        className: "bg-as-surface-secondary relative h-10 w-10 rounded-full",
        children: isProcessing ? _jsx(motion.div, {
          initial: {
            opacity: 0,
            scale: 0.3
          },
          animate: {
            opacity: 1,
            scale: 1
          },
          transition: {
            duration: 0.5,
            ease: "easeInOut",
            delay
          },
          className: "border-as-border-secondary absolute z-10 m-2 flex h-6 w-6 items-center justify-center rounded-full border-2 shadow-lg backdrop-blur-sm",
          children: _jsx(Loader2, {
            className: "text-as-primary h-4 w-4 animate-spin"
          })
        }) : _jsx(motion.div, {
          initial: {
            opacity: 0,
            scale: 0.3
          },
          animate: {
            opacity: 1,
            scale: 1
          },
          transition: {
            duration: 0.5,
            ease: "easeOut",
            delay
          },
          className: "bg-as-success-secondary absolute z-10 m-2 flex h-6 w-6 items-center justify-center rounded-full border border-white/30 shadow-lg backdrop-blur-sm",
          children: _jsx(CheckIcon, {
            className: "text-as-content-icon-success h-4 w-4"
          })
        })
      }), _jsx(motion.div, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        transition: {
          duration: 0.3,
          ease: "easeInOut",
          delay
        },
        className: "shrink-0 text-base",
        children: isProcessing ? _jsx(TextShimmer, {
          duration: 1,
          children: title
        }) : _jsx("span", {
          className: "text-as-primary",
          children: title
        })
      })]
    }), _jsx("div", {
      className: "flex flex-col gap-1",
      children: tx ? _jsx(motion.div, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        transition: {
          duration: 0.3,
          ease: "easeInOut",
          delay: (delay || 0) + 0.3
        },
        className: "flex items-center gap-3",
        children: _jsx("a", {
          href: getExplorerTxUrl(chainId, tx.txHash),
          target: "_blank",
          children: _jsx("div", {
            className: "text-as-primary/30 font-mono text-xs",
            children: centerTruncate(tx?.txHash, 6)
          })
        })
      }) : null
    })]
  });
}
export const OrderDetailsLoadingView = _jsxs("div", {
  className: "mx-auto flex w-full max-w-[460px] flex-col items-center gap-4",
  children: [_jsxs(Badge, {
    variant: "default",
    className: "hover:bg-b3-react-background flex items-center gap-3 border-white/20 bg-white/10 px-4 py-1 text-base transition-colors",
    children: [_jsx(Loader2, {
      className: "text-as-primary h-4 w-4 animate-spin"
    }), _jsx(TextShimmer, {
      duration: 1,
      className: "font-sf-rounded text-base font-semibold",
      children: "Loading..."
    })]
  }), _jsxs("div", {
    className: "flex w-full flex-1 flex-col",
    children: [_jsxs("div", {
      className: "mb-4 flex flex-col gap-1",
      children: [_jsx(Skeleton, {
        className: "h-4 w-24"
      }), _jsxs("div", {
        className: "mt-2 flex items-center gap-2",
        children: [_jsx(Skeleton, {
          className: "h-8 w-48"
        }), _jsx(Skeleton, {
          className: "ml-4 h-8 w-32"
        })]
      }), _jsx(Skeleton, {
        className: "mt-4 h-8 w-24"
      })]
    }), _jsx(Skeleton, {
      className: "mb-4 h-12 w-full"
    }), _jsxs("div", {
      className: "flex w-full items-center justify-between gap-4",
      children: [_jsxs(Skeleton, {
        className: "rounded-lg p-6 pb-3",
        children: [_jsx("div", {
          className: "h-[200px] w-[200px]"
        }), _jsx("div", {
          className: "mt-3 flex items-center justify-center gap-2",
          children: _jsx("div", {
            className: "h-5 w-5 rounded-full"
          })
        })]
      }), _jsx("div", {
        className: "flex flex-1 flex-col gap-2",
        children: [1, 2, 3].map(i => _jsx(Skeleton, {
          className: "h-10 w-full"
        }, i))
      })]
    })]
  }), _jsxs("div", {
    className: "bg-b3-react-background mt-8 w-full rounded-lg p-4",
    children: [_jsx(Skeleton, {
      className: "mb-3 h-4 w-48"
    }), _jsxs("div", {
      className: "flex items-center gap-4",
      children: [_jsx(Skeleton, {
        className: "h-10 flex-1"
      }), _jsx(Skeleton, {
        className: "h-10 flex-1"
      })]
    })]
  }), _jsx(Skeleton, {
    className: "h-10 w-full"
  })]
});